/**
 * Song learning (completion plan P5.5).
 *
 * The catalog lists ONLY rights-cleared pieces; the detail is the server's
 * rights-gate projection — a field whose capability is not granted is absent.
 * A speak-along attempt starts through the ordinary coaching API
 * (`createCoachingSession` with `songPractice`), on the canonical runtime.
 */
import { api } from '../api';

export type SongCapability = 'display_lyrics' | 'translate' | 'audio_playback' | 'session_practice' | 'recording_share';
export type RightsBasis = 'original' | 'public_domain' | 'licensed' | 'none';

export interface SongSummary {
  ref: string;
  title: string;
  language: string;
  level: 'beginner' | 'intermediate' | 'advanced';
  about: string;
  rightsBasis: RightsBasis;
  sectionCount: number;
}

export interface SongSectionView {
  id: string;
  label: string;
  context: string;
  /** Present only when display_lyrics is granted. */
  lines?: string[];
  notes?: string[];
  /** Present only when translate is granted. */
  gloss?: string[];
  timing: { startMs: number | null; endMs: number | null };
}

export interface SongView {
  ref: string;
  title: string;
  language: string;
  level: SongSummary['level'];
  about: string;
  credit: string;
  rightsBasis: RightsBasis;
  provenance: string;
  capabilities: Partial<Record<SongCapability, boolean>>;
  sections: SongSectionView[];
  pronunciation: { status: 'UNAVAILABLE'; awaiting: string };
  rhythm: { status: 'UNAVAILABLE'; awaiting: string };
}

export interface SongDependencies {
  licensedProvider: { status: string; decision: string; owner: string };
  pronunciationAndRhythm: { status: 'UNAVAILABLE'; awaiting: string };
  challenge: { kind: string; status: 'GATED'; reason: string };
}

/** What the learner sends to start a speak-along. Names only; the server supplies the words. */
export interface SongPracticeRequest {
  songRef: string;
  sectionId: string;
}

interface Envelope<T> {
  success: boolean;
  data: T;
}

// The tag is registered here, in this slice, not on the shared tagTypes line.
export const songLearningApi = api.enhanceEndpoints({ addTagTypes: ['SongLearning'] }).injectEndpoints({
  endpoints: (builder) => ({
    getSongCatalog: builder.query<{ songs: SongSummary[]; dependencies: SongDependencies }, void>({
      query: () => '/coaching/song-learning',
      transformResponse: (r: Envelope<{ songs: SongSummary[]; dependencies: SongDependencies }>) => r.data,
      providesTags: ['SongLearning'],
    }),
    getSong: builder.query<{ song: SongView; dependencies: SongDependencies }, string>({
      query: (ref) => `/coaching/song-learning/${encodeURIComponent(ref)}`,
      transformResponse: (r: Envelope<{ song: SongView; dependencies: SongDependencies }>) => r.data,
      providesTags: ['SongLearning'],
    }),
  }),
  overrideExisting: false,
});

export const { useGetSongCatalogQuery, useGetSongQuery } = songLearningApi;
